import type { AssetEnergySummary } from '@einwert/valuation-api-client'
import { FUEL_TYPE_LABELS, getFormattedValueAndUnitFromNumber, joinDefinedStrings, MetricGrid, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Box, Divider, Stack } from '@mui/material'
import { EnergyEfficiencyLoader } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/Widgets/EnergyEfficiency.load'
import { uniq } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type EnergyEfficiencyProps = {
    energySummary?: AssetEnergySummary
    isPending?: boolean
}

export const EnergyEfficiency = ({ energySummary, isPending }: EnergyEfficiencyProps) => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()

    if (isPending) {
        return <EnergyEfficiencyLoader />
    }

    const allFuelTypes = uniq(
        energySummary?.fuel_types?.map((fuelType) => {
            return FUEL_TYPE_LABELS[fuelType] || fuelType
        }) || []
    )

    const gridValues = [
        {
            ...getFormattedValueAndUnitFromNumber(
                energySummary?.primary_power_consumption,
                tCommon('kilowattHoursPerSquareMeterPerYear'),
                undefined,
                locale
            ),
            subheadline: tCommon('primaryPowerConsumption'),
        },
        {
            value: joinDefinedStrings(allFuelTypes, ', ') || '-',
            subheadline: tCommon('heatingType'),
        },
        {
            ...getFormattedValueAndUnitFromNumber(energySummary?.total_carbon_dioxide_equivalent, 'kg', undefined, locale),
            subheadline: tCommon('carbonDioxideEquivalent'),
        },
    ]

    return (
        <Stack direction="column" width="100%" gap={tokens.SizingSpacing300} alignItems="center">
            <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                {tCommon('energyEfficiencyClass')}
            </Typography>
            <Stack direction="column" width="100%" gap={tokens.SizingSpacing400} alignItems="center">
                <Stack direction="row" gap={tokens.SizingSpacing050}>
                    {[
                        { key: 'a_plus', label: 'A', suffix: '+', color: '#68933D' },
                        { key: 'a', label: 'A', color: '#77A330' },
                        { key: 'b', label: 'B', color: '#93B94A' },
                        { key: 'c', label: 'C', color: '#BBCD7F' },
                        { key: 'd', label: 'D', color: '#F6CC06' },
                        { key: 'e', label: 'E', color: '#F4B614' },
                        { key: 'f', label: 'F', color: '#EEA213' },
                        { key: 'g', label: 'G', color: '#DB6B28' },
                        { key: 'h', label: 'H', color: '#DA5138' },
                    ].map((energyClass) =>
                        energySummary?.energy_efficiency_classes.includes(energyClass.key) ? (
                            <FilledColorBox
                                key={energyClass.key}
                                color={energyClass.color}
                                label={energyClass.label}
                                suffix={energyClass.suffix}
                            />
                        ) : (
                            <EmptyColorBox key={energyClass.key} color={energyClass.color} />
                        )
                    )}
                </Stack>
                <Stack direction="column" gap={tokens.SizingSpacing100} width="100%">
                    <Divider sx={{ width: '100%', borderColor: tokens.ColorBorderDefault }} />
                    <MetricGrid size="small" variant="spacious" numColumns={3} values={gridValues} />
                </Stack>
            </Stack>
        </Stack>
    )
}

const EmptyColorBox = ({ color }: { color: string }) => (
    <Box border={`${tokens.SizingBorderM} solid ${color}`} height="40px" width="32px" borderRadius={tokens.SizingCornerS} />
)

const FilledColorBox = ({ color, label, suffix }: { color: string; label: string; suffix?: string }) => (
    <Box
        bgcolor={color}
        height="40px"
        width="34px"
        borderRadius={tokens.SizingCornerS}
        display="flex"
        alignItems="center"
        justifyContent="center"
    >
        <Typography variant="valueS" color={tokens.ColorTextInverse}>
            {label}
        </Typography>
        {suffix && (
            <Typography variant="headline2" color={tokens.ColorTextInverse}>
                {suffix}
            </Typography>
        )}
    </Box>
)
